import { API_BASE_URL } from '../config/api';

export interface BooksUploadProgress {
  loaded: number;
  total: number;
  percent: number;
}


interface BooksServiceConfig {
  baseUrl: string;
  tokenKey: string;
  userKey: string;
}

const defaultBooksServiceConfig: BooksServiceConfig = {
  baseUrl: API_BASE_URL,
  tokenKey: 'haapa.authToken',
  userKey: 'haapa.currentUser',
};

class BooksService {
  private baseUrl: string;
  private tokenKey: string;
  private userKey: string;

  constructor(config = defaultBooksServiceConfig) {
    this.baseUrl = config.baseUrl;
    this.tokenKey = config.tokenKey;
    this.userKey = config.userKey;
  }

  private asRecord(value: unknown): Record<string, unknown> {
    return value && typeof value === 'object' ? (value as Record<string, unknown>) : {};
  }

  private asString(value: unknown): string | undefined {
    return typeof value === 'string' && value.trim() ? value.trim() : undefined;
  }

  private buildHeaders(): HeadersInit {
    const headers: Record<string, string> = {
      Accept: '*/*',
    };

    if (typeof window === 'undefined') {
      return headers;
    }

    const token = window.localStorage.getItem(this.tokenKey);
    const rawUser = window.localStorage.getItem(this.userKey);

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    if (rawUser) {
      try {
        const parsedUser = this.asRecord(JSON.parse(rawUser));
        const userId = this.asString(parsedUser.id);
        const role = this.asString(parsedUser.role);
        if (userId) {
          headers['x-user-id'] = userId;
        }
        if (role) {
          headers['x-user-role'] = role;
        }
      } catch {
        // Ignore malformed user cache
      }
    }

    return headers;
  }

  private toErrorMessage(payload: unknown, fallback: string): string {
    const record = this.asRecord(payload);
    const dataRecord = this.asRecord(record.data);
    const message =
      this.asString(record.message) ||
      this.asString(dataRecord.message) ||
      this.asString(record.error) ||
      this.asString(dataRecord.error);

    return message ?? fallback;
  }

  private async parseResponse(response: Response): Promise<unknown> {
    let data: unknown = null;
    try {
      data = await response.json();
    } catch {
      data = null;
    }

    if (!response.ok) {
      const fallback = `Request failed with status ${response.status}`;
      throw new Error(this.toErrorMessage(data, fallback));
    }

    return data;
  }

  private async postFormDataWithProgress(
    url: string,
    formData: FormData,
    onUploadProgress: (progress: BooksUploadProgress) => void,
  ): Promise<unknown> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', url);

      const headers = this.buildHeaders();
      for (const [key, value] of Object.entries(headers)) {
        xhr.setRequestHeader(key, value);
      }

      xhr.upload.onprogress = (event) => {
        if (!event.lengthComputable) {
          return;
        }
        const percent = event.total > 0 ? (event.loaded / event.total) * 100 : 0;
        onUploadProgress({ loaded: event.loaded, total: event.total, percent });
      };

      xhr.onerror = () => reject(new Error('Network error while uploading media.'));
      xhr.onabort = () => reject(new Error('Upload aborted.'));
      xhr.onload = () => {
        let payload: unknown = null;
        try {
          payload = xhr.responseText ? (JSON.parse(xhr.responseText) as unknown) : null;
        } catch {
          payload = null;
        }

        if (xhr.status >= 200 && xhr.status < 300) {
          resolve(payload);
          return;
        }

        const fallback = `Request failed with status ${xhr.status}`;
        reject(new Error(this.toErrorMessage(payload, fallback)));
      };

      xhr.send(formData);
    });
  }

  async createBook(payload: {
    titleKey: string;
    descriptionKey: string;
    price: number;
    categoryKey: string;
    language?: string;
    audience?: string;
    isPOD?: boolean;
    bookFile?: File | null;
    coverFile?: File | null;
    audioFile?: File | null;
    videoFile?: File | null;
    trailerFile?: File | null;
  }): Promise<Product> {
    const formData = new FormData();
    formData.append('name', payload.titleKey);
    formData.append('titleKey', payload.titleKey);
    formData.append('summary', payload.descriptionKey);
    formData.append('descriptionKey', payload.descriptionKey);
    formData.append('price', String(payload.price));
    formData.append('category', payload.categoryKey);
    formData.append('categoryKey', payload.categoryKey);
    if (payload.language) formData.append('language', payload.language);
    if (payload.audience) formData.append('audience', payload.audience);
    if (payload.isPOD !== undefined) formData.append('isPOD', String(payload.isPOD));

    if (payload.bookFile) formData.append('bookFile', payload.bookFile);
    if (payload.coverFile) formData.append('coverFile', payload.coverFile);
    if (payload.audioFile) formData.append('audioFile', payload.audioFile);
    if (payload.videoFile) formData.append('videoFile', payload.videoFile);
    if (payload.trailerFile) formData.append('trailerFile', payload.trailerFile);

    const response = await fetch(`${this.baseUrl}/books`, {
      method: 'POST',
      headers: this.buildHeaders(),
      body: formData,
    });

    const data = await this.parseResponse(response);
    const record = this.asRecord(data);
    const result = record.data || data;
    
    // We need to map this back to a Product type.
    // Since normalizeProductFromApi is in ShopContext (not ideal for service), 
    // we'll return the raw result and let the component handle it or provide a basic mapping.
    return result as any;
  }
}

export const booksService = new BooksService();
